/**
 * Enormous numbers, and what they are called.
 *
 * Doubling runs past what a JavaScript number can hold in about fifty taps, so
 * this works in BigInt throughout. A child who keeps pressing "double it"
 * should get a correct answer at the four-hundredth press, not a rounded one —
 * being able to go on forever is the entire point of the toy.
 */

/** Short-scale names, index n meaning 10^(3n+3). */
const SCALES = [
  'thousand',
  'million',
  'billion',
  'trillion',
  'quadrillion',
  'quintillion',
  'sextillion',
  'septillion',
  'octillion',
  'nonillion',
  'decillion',
  'undecillion',
  'duodecillion',
  'tredecillion',
  'quattuordecillion',
  'quindecillion',
  'sexdecillion',
  'septendecillion',
  'octodecillion',
  'novemdecillion',
  'vigintillion',
];

export function digitCount(n: bigint): number {
  const s = (n < 0n ? -n : n).toString();
  return s.length;
}

/** "4,294,967,296" — grouped so the eye can find the thousands. */
export function groupDigits(n: bigint): string {
  const negative = n < 0n;
  const digits = (negative ? -n : n).toString();
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ',';
    out += digits[i];
  }
  return negative ? `-${out}` : out;
}

/**
 * The name of the size the number has reached — "million", "billion" — or null
 * while it is still small enough to just read out.
 */
export function scaleName(n: bigint): string | null {
  const digits = digitCount(n < 0n ? -n : n);
  if (digits < 4) return null;
  const index = Math.floor((digits - 1) / 3) - 1;
  if (index < SCALES.length) return SCALES[index];
  // Past the names people actually use, size is better said in digits.
  return null;
}

/**
 * How a child would say it out loud: "4 billion", "27 million", "350". Rounded
 * on purpose — the exact digits are already on screen, and "four thousand two
 * hundred and ninety-four million" helps nobody.
 */
export function roughName(n: bigint): string {
  const negative = n < 0n;
  const abs = negative ? -n : n;
  const scale = scaleName(abs);
  if (!scale) {
    const digits = digitCount(abs);
    if (digits > 63) return `${digits} digits long`;
    return groupDigits(n);
  }
  const digits = digitCount(abs);
  const power = BigInt(10) ** BigInt(Math.floor((digits - 1) / 3) * 3);
  const lead = abs / power;
  return `${negative ? '-' : ''}${lead} ${scale}`;
}

/** Comparisons a child has a feel for, in ascending order. */
const LANDMARKS: Array<{ at: bigint; text: string }> = [
  { at: 100n, text: 'about the number of steps to the end of your road' },
  { at: 1_000n, text: 'about how many words you say in ten minutes' },
  { at: 20_000n, text: 'about how many breaths you take in a day' },
  { at: 1_000_000n, text: 'about how many letters are in a hundred books' },
  { at: 100_000_000n, text: 'more than every second in three whole years' },
  { at: 8_000_000_000n, text: 'about how many people are alive right now' },
  { at: 100_000_000_000n, text: 'about how many stars are in our galaxy' },
  { at: 37_000_000_000_000n, text: 'about how many cells are in your body' },
  { at: 1_000_000_000_000_000_000n, text: 'more grains than all the sand on a beach' },
];

/** Something the size can be held up against, or null when it is off the chart. */
export function landmarkFor(n: bigint): string | null {
  if (n < 100n) return null;
  let found: string | null = null;
  for (const mark of LANDMARKS) {
    if (n >= mark.at) found = mark.text;
  }
  if (n > 10n ** 21n) return 'more than anyone has ever counted';
  return found;
}

export type Operation = 'double' | 'times10' | 'square' | 'half' | 'plus1';

export function apply(n: bigint, op: Operation): bigint {
  switch (op) {
    case 'double':
      return n * 2n;
    case 'times10':
      return n * 10n;
    case 'square':
      return n * n;
    case 'half':
      return n / 2n;
    case 'plus1':
      return n + 1n;
  }
}

/**
 * Squaring a thousand-digit number produces a two-thousand-digit one, and a
 * few more presses would lock up the tab formatting it. The toy stops offering
 * the button rather than freezing.
 */
export const MAX_DIGITS = 1000;

export function canApply(n: bigint, op: Operation): boolean {
  if (op === 'half') return n > 1n;
  const digits = digitCount(n);
  if (op === 'square') return digits * 2 <= MAX_DIGITS;
  return digits + 1 <= MAX_DIGITS;
}
